// src/components/core/MigrationModal/tabs/MigratorTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useEffect, useRef } from 'react';
import {
  Card,
  Space,
  Typography,
  Switch,
  Tooltip,
  Progress,
  Row,
  Col,
  Alert,
  Descriptions,
  theme,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  MinusCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  DatabaseOutlined,
  FileSyncOutlined,
  DollarOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const MigratorTab = ({
  hasToken,
  locked,
  data = {},
  isProcessing = false,
  isRefreshingCounts = false,
  onToggleAutoMigrate,
  // Credits props
  hasEnrichmentEnabled = false,
  creditsData = null,
  isLoadingCredits = false,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // HOOKS
  // ─────────────────────────────────────────────────────────────────────────────
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen', false);

  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    autoMigrate = false,
    totalRecords = 0,
    migratedRecords = 0,
    updatedRecords = 0,
    skippedRecords = 0,
    lastMigratedAt,
    delayBetweenRecordsMs = 20000,
  } = data;

  // Handler to open credits drawer
  const handleOpenCreditsDrawer = () => {
    setSettingsDrawerOpen(true);
  };

  // Calculate remaining (moved up because it's used below)
  const remainingRecords = useMemo(() => {
    return Math.max(0, totalRecords - migratedRecords - updatedRecords - skippedRecords);
  }, [totalRecords, migratedRecords, updatedRecords, skippedRecords]);

  // Credits calculations
  const creditsRequired = creditsData?.credits?.required || 0;
  const creditsAvailable = creditsData?.credits?.available || 0;
  const creditsSufficient = creditsData?.credits?.sufficient !== false;
  const creditsDeficit = creditsData?.credits?.deficit || 0;
  const estimatedPriceBRL = creditsData?.estimate?.estimatedPriceBRL || 0;
  const recordsThatCanBeProcessed = creditsData?.credits?.recordsThatCanBeProcessed || 0;
  const recordsToEnrich = creditsData?.estimate?.remainingRecords || remainingRecords;
  // Per-record cost info
  const perRecordCredits = creditsData?.credits?.perRecord || 0;
  const perRecordPriceBRL =
    creditsData?.credits?.perRecordBRL || creditsData?.estimate?.perRecordPriceBRL || 0;

  // Track states for notification logic
  const prevAutoMigrateRef = useRef(autoMigrate);
  const notificationShownRef = useRef(false);
  const autoMigrateJustTurnedOffRef = useRef(false);

  // Detect when autoMigrate turns OFF and set a flag
  useEffect(() => {
    const wasAutoMigrate = prevAutoMigrateRef.current;
    const isAutoMigrate = autoMigrate;

    // When autoMigrate changes from ON to OFF, set flag for notification check
    if (wasAutoMigrate === true && isAutoMigrate === false) {
      autoMigrateJustTurnedOffRef.current = true;
    }

    // Reset flags when autoMigrate turns ON
    if (isAutoMigrate) {
      notificationShownRef.current = false;
      autoMigrateJustTurnedOffRef.current = false;
    }

    prevAutoMigrateRef.current = isAutoMigrate;
  }, [autoMigrate]);

  // Show notification when credits data shows insufficient credits after autoMigrate was turned off
  useEffect(() => {
    // Check if credits are insufficient for processing
    const cannotProcessMoreRecords = recordsThatCanBeProcessed === 0 && perRecordCredits > 0;

    // Show notification when:
    // 1. Enrichment is enabled
    // 2. autoMigrate was recently turned OFF (flag set)
    // 3. Cannot process more records (insufficient credits)
    // 4. Notification hasn't been shown yet
    // 5. autoMigrate is currently OFF
    // 6. We have credits data
    if (
      hasEnrichmentEnabled &&
      autoMigrateJustTurnedOffRef.current &&
      cannotProcessMoreRecords &&
      !notificationShownRef.current &&
      !autoMigrate &&
      creditsData !== null
    ) {
      appService.notification({
        flag: 'warning',
        duration: 0, // User must close manually
        title: 'migration_paused_no_credits',
        description: 'migration_paused_no_credits_description',
      });
      notificationShownRef.current = true;
      autoMigrateJustTurnedOffRef.current = false; // Clear flag after showing
    }
  }, [recordsThatCanBeProcessed, perRecordCredits, hasEnrichmentEnabled, creditsData, autoMigrate]);

  // Should we disable the switch due to insufficient credits?
  // Only disable if there are NO credits to process even 1 record
  // (if user has credits for at least 1 record, allow autoMigrate - system will pause when credits run out)
  const insufficientCredits =
    hasEnrichmentEnabled && recordsThatCanBeProcessed === 0 && creditsRequired > 0;

  // Calculate progress percentage
  const percentage = useMemo(() => {
    if (totalRecords === 0) return 0;
    const processed = migratedRecords + updatedRecords + skippedRecords;
    return Math.round((processed / totalRecords) * 100);
  }, [totalRecords, migratedRecords, updatedRecords, skippedRecords]);

  // Calculate ETA
  const etaText = useMemo(() => {
    if (!autoMigrate || remainingRecords === 0) return null;
    const secondsRemaining = remainingRecords * (delayBetweenRecordsMs / 1000);
    const hours = Math.floor(secondsRemaining / 3600);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `~${days} ${translateX(days === 1 ? 'day' : 'days')}`;
    }
    if (hours > 0) {
      return `~${hours} ${translateX(hours === 1 ? 'hour' : 'hours')}`;
    }
    const minutes = Math.ceil(secondsRemaining / 60);
    return `~${minutes} ${translateX(minutes === 1 ? 'minute' : 'minutes')}`;
  }, [autoMigrate, remainingRecords, delayBetweenRecordsMs]);

  // Format last migrated date
  // When autoMigrate is ON: show relative time ("ha poucos segundos")
  // When autoMigrate is OFF: show absolute date/time ("DD/MM/YYYY HH:mm:ss")
  const lastMigratedText = useMemo(() => {
    if (!lastMigratedAt) return translateX('never');
    if (autoMigrate) {
      return dayjs(lastMigratedAt).fromNow();
    }
    return dayjs(lastMigratedAt).format('DD/MM/YYYY HH:mm:ss');
  }, [lastMigratedAt, autoMigrate]);

  // Progress status
  const progressStatus = useMemo(() => {
    if (isProcessing) return 'active';
    if (percentage === 100) return 'success';
    return 'normal';
  }, [isProcessing, percentage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleToggleAutoMigrate = checked => {
    onToggleAutoMigrate?.(checked);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  const { token: antToken } = theme.useToken();

  return (
    <div style={{ paddingTop: 8 }}>
      <Card
        size="small"
        title={
          <Space>
            <FileSyncOutlined style={{ color: antToken.colorPrimary }} />
            <span className="text-muted">{translateX('automatic_migration')}</span>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {autoMigrate ? translateX('auto_migration_on') : translateX('auto_migration_off')}
            </Text>
            <Tooltip
              title={
                !hasToken
                  ? translateX('configure_token_first')
                  : insufficientCredits
                    ? translateX('insufficient_credits_to_start')
                    : autoMigrate
                      ? translateX('stop_auto_migration')
                      : translateX('start_auto_migration')
              }
            >
              <Switch
                size="small"
                checked={autoMigrate}
                onChange={handleToggleAutoMigrate}
                disabled={
                  locked || !hasToken || totalRecords === 0 || (!autoMigrate && insufficientCredits)
                }
                loading={isProcessing && autoMigrate}
              />
            </Tooltip>
          </Space>
        }
        styles={{
          body: {
            opacity: hasToken ? 1 : 0.5,
            pointerEvents: hasToken ? 'auto' : 'none',
          },
        }}
      >
        {/* Processing status */}
        {isProcessing && autoMigrate && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <SyncOutlined spin style={{ marginRight: 6 }} />
              {translateX('migration_in_progress')}...
            </Text>
          </div>
        )}

        {/* Statistics - Responsive layout (horizontal desktop, vertical mobile) */}
        <Row gutter={[8, 8]} className="mt-3">
          {/* Total */}
          <Col xs={24} sm={6} className="my-2">
            <Tooltip title={translateX('total_records_tooltip')}>
              <div style={{ textAlign: 'center', cursor: 'normal' }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>
                  {isRefreshingCounts ? (
                    <SyncOutlined spin style={{ marginRight: 4 }} />
                  ) : (
                    <DatabaseOutlined style={{ marginRight: 4 }} />
                  )}
                  Total
                </div>
                <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>
                  {isRefreshingCounts ? '...' : totalRecords.toLocaleString()}
                </div>
              </div>
            </Tooltip>
          </Col>

          {/* Migrated - MIGRADOS */}
          <Col xs={24} sm={6} className="my-2">
            <Tooltip title={translateX('migrated_records_tooltip')}>
              <div style={{ textAlign: 'center', cursor: 'normal' }}>
                <div style={{ fontSize: 11, color: '#52c41a', marginBottom: 2 }}>
                  <CheckCircleOutlined style={{ marginRight: 4 }} />
                  {translateX('migrated_short')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#52c41a', lineHeight: 1.2 }}>
                  {migratedRecords.toLocaleString()}
                </div>
              </div>
            </Tooltip>
          </Col>

          {/* Updated - ATUALIZADOS */}
          <Col xs={24} sm={6} className="my-2">
            <Tooltip title={translateX('updated_records_tooltip')}>
              <div style={{ textAlign: 'center', cursor: 'normal' }}>
                <div style={{ fontSize: 11, color: '#faad14', marginBottom: 2 }}>
                  <EditOutlined style={{ marginRight: 4 }} />
                  {translateX('updated_short')}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    color: updatedRecords > 0 ? '#faad14' : undefined,
                    lineHeight: 1.2,
                  }}
                >
                  {updatedRecords.toLocaleString()}
                </div>
              </div>
            </Tooltip>
          </Col>

          {/* Skipped - IGNORADOS */}
          <Col xs={24} sm={6} className="my-2">
            <Tooltip title={translateX('skipped_records_tooltip')}>
              <div style={{ textAlign: 'center', cursor: 'normal' }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>
                  <MinusCircleOutlined style={{ marginRight: 4 }} />
                  {translateX('skipped_short')}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    color: skippedRecords > 0 ? '#8c8c8c' : undefined,
                    lineHeight: 1.2,
                  }}
                >
                  {skippedRecords.toLocaleString()}
                </div>
              </div>
            </Tooltip>
          </Col>
        </Row>

        {/* Progress Bar */}
        <div style={{ margin: '24px 12px 0' }}>
          <Progress
            percent={percentage}
            status={progressStatus}
            size="small"
            format={percent => `${percent}%`}
          />
        </div>

        {/* Footer info - Last migration and ETA */}
        <div
          style={{ marginTop: 4, marginLeft: 12, display: 'flex', justifyContent: 'space-between' }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {translateX('last_migration')}: {lastMigratedText}
          </Text>
          {etaText && autoMigrate && remainingRecords > 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {translateX('remaining')}: {etaText}
            </Text>
          )}
        </div>

        {/* Info Alert at the bottom */}
        <Alert
          type="info"
          showIcon
          description={translateX('migration_time_info')}
          style={{ marginTop: 24 }}
        />
      </Card>

      {/* Credits Info Card - Only show if enrichment is enabled */}
      {hasEnrichmentEnabled && hasToken && (
        <Card
          size="small"
          style={{ marginTop: 16 }}
          title={
            <Space>
              <DollarOutlined style={{ color: antToken.colorPrimary }} />
              <span className="text-muted">{translateX('estimated_enrichment_cost')}</span>
            </Space>
          }
          loading={isLoadingCredits}
        >
          <Descriptions bordered size="small" column={1}>
            {/*<Descriptions.Item*/}
            {/*  label={translateX('estimated_cost')}*/}
            {/*  styles={{*/}
            {/*    content: { textAlign: 'right' },*/}
            {/*  }}*/}
            {/*>*/}
            {/*  {financialService.formatCurrency(estimatedPriceBRL)}*/}
            {/*</Descriptions.Item>*/}

            {/* record to enrich */}
            <Descriptions.Item
              label={translateX('records_to_enrich')}
              styles={{
                content: { textAlign: 'right' },
              }}
            >
              {recordsToEnrich.toLocaleString()}
            </Descriptions.Item>
            {/* record to enrich */}

            {/* value per register */}
            <Descriptions.Item
              label={translateX('credits_per_register')}
              styles={{
                content: { textAlign: 'right' },
              }}
            >
              {perRecordCredits}
              {/*/ {financialService.formatCurrency(perRecordPriceBRL)}*/}
            </Descriptions.Item>
            {/* value per register */}

            <Descriptions.Item
              label={translateX('credits_available_title')}
              styles={{
                content: { textAlign: 'right', fontWeight: 600 },
              }}
            >
              {creditsAvailable.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item
              label={translateX('records_can_process')}
              styles={{
                content: { textAlign: 'right' },
              }}
            >
              {recordsThatCanBeProcessed.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item
              label={translateX('missing_credits')}
              styles={{
                content: { textAlign: 'right', color: !creditsSufficient ? '#fa2222' : 'unset' },
              }}
            >
              {creditsSufficient
                ? translateX('sufficient_credits')
                : creditsDeficit.toLocaleString()}
            </Descriptions.Item>
          </Descriptions>

          {/* Insufficient credits warning */}
          {!creditsSufficient && creditsRequired > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              title={
                <span>
                  {translateX('your_balance_is_plain')}{' '}
                  <strong>{creditsAvailable.toLocaleString()}</strong> {translateX('credits')}.{' '}
                  <a onClick={handleOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
              style={{ marginTop: 12 }}
            />
          )}
        </Card>
      )}

      {/* Warning if no token */}
      {!hasToken && (
        <Alert
          type="warning"
          showIcon
          title={translateX('token_required')}
          description={translateX('configure_token_first_migrator')}
          style={{ marginTop: 16 }}
        />
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MigratorTab;
